/**
 * useWelcomeAnalytics.js
 *
 * Analytics hook for the Diet Planner's Screen 1 (Welcome).
 * Mirrors useYogAnalytics.js exactly — same GA4 wrapper via react-ga4, same
 * IS_PRODUCTION guard (excludes localhost + 127.0.0.1), same dev-mode
 * console.group/console.table logging so events are inspectable without
 * opening the GA4 dashboard.
 *
 * HOW TO USE:
 *   const analytics = useWelcomeAnalytics();
 *   analytics.trackAssessmentStart(sessionId);
 *
 * DEV MODE:
 *   All events are console.group'd with the [WelcomeAnalytics] prefix.
 *   Open browser DevTools → Console to see all events in real time.
 */

import { useCallback, useRef } from "react";
import ReactGA from "react-ga4";

// ─── Production guard ──────────────────────────────────────────────────────────
// Matches useYogAnalytics.js: excludes both localhost and 127.0.0.1 since the
// CRA dev server can serve on either hostname.
const IS_PRODUCTION =
  process.env.NODE_ENV === "production" &&
  typeof window !== "undefined" &&
  window.location.hostname !== "localhost" &&
  window.location.hostname !== "127.0.0.1";

// ─── Core send helper ──────────────────────────────────────────────────────────
const send = (eventName, params = {}) => {
  // Strip undefined / null / empty values so the GA4 payload stays clean
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
  );

  if (IS_PRODUCTION) {
    ReactGA.event(eventName, cleanParams);
  } else {
    // eslint-disable-next-line no-console
    console.group(`%c[WelcomeAnalytics] ${eventName}`, "color:#2E7D32;font-weight:bold;");
    // eslint-disable-next-line no-console
    if (Object.keys(cleanParams).length > 0) console.table(cleanParams);
    // eslint-disable-next-line no-console
    else console.log("(no params)");
    // eslint-disable-next-line no-console
    console.groupEnd();
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════════════════

const useWelcomeAnalytics = () => {
  // Guards against firing 'assessment_start' more than once per session,
  // even if the hook instance is re-created (e.g. StrictMode double-invoke).
  const startFiredRef = useRef(false);

  // ── Page lifecycle ───────────────────────────────────────────────────────────

  const trackPageView = useCallback((sessionId) => {
    send("welcome_page_view", { session_id: sessionId });
  }, []);

  const trackTrustMarkersRendered = useCallback((sessionId) => {
    send("welcome_trust_markers_rendered", { session_id: sessionId });
  }, []);

  // ── Primary CTA ───────────────────────────────────────────────────────────────
  // Fires once per anonymous session — the caller is responsible for passing
  // a stable session_id (see Welcome.js's getOrCreateSessionId()).

  const trackAssessmentStart = useCallback((sessionId) => {
    if (startFiredRef.current) return;
    startFiredRef.current = true;
    send("assessment_start", {
      session_id: sessionId,
      ts: new Date().toISOString(),
    });
  }, []);

  return {
    trackPageView,
    trackTrustMarkersRendered,
    trackAssessmentStart,
  };
};

export default useWelcomeAnalytics;